// Podiatry orthotic order form — the portal half of the Forma-Podiatry workflow.
// Opened from the Forma tab on FEET cases. Mirrors the clinic's RxIx/FootSecure
// order sheet: clinic + patient details, case sheet, arch/mobility exam,
// 14-parameter measurement table (R/L), pathology photos from the iOS quick
// capture, offloading, recommendations, footwear, artifacts, delivery date —
// plus a printable order PDF.
//
// Measurements: P1 = manual entry (stored as manualJson overrides per foot).
// P2 prefills the Auto column from the Reconstructor; a manual value always wins.

import { useState, useEffect, useRef, useCallback } from 'react'
import {
  getPodiatryCase, savePodiatryIntake, savePodiatryMeasurements,
  deletePodiatryPhoto, fetchPodiatryPhotoBlob,
  podiatryArtifactsBatchInit, podiatryArtifactsBatchComplete,
} from '../services/api'
import {
  PODIATRY_PARAMS, PODIATRY_MODIFICATIONS, ARCH_TYPES, MOBILITY_TYPES,
  PHOTO_VIEWS, CONDITION_LABELS,
} from './podiatryParams'

const FEET = ['RIGHT', 'LEFT']   // form order: Right column first (matches the paper sheet)

export default function PodiatryFormPanel({ caseId, onClose }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [savedTick, setSavedTick] = useState(false)
  const [data, setData] = useState(null)          // GET response (intake, sets, catalogue)
  const [form, setForm] = useState({})            // intake fields being edited
  const [meas, setMeas] = useState({ RIGHT: {}, LEFT: {} })  // paramKey -> string value
  const [dirtyMeas, setDirtyMeas] = useState({ RIGHT: new Set(), LEFT: new Set() })
  const [photoUrls, setPhotoUrls] = useState({})  // photoId -> objectURL
  const photoUrlsRef = useRef({})                  // mirror for stable cleanup (avoids stale-closure revokes)
  const [pdfBusy, setPdfBusy] = useState(false)
  const [artifactBusy, setArtifactBusy] = useState(false)
  const fileInputRef = useRef(null)
  const savedFormRef = useRef('{}')                // JSON snapshot of the last-loaded form (dirty check)
  const params = data?.catalogue?.params || PODIATRY_PARAMS
  const modifications = data?.catalogue?.modifications || PODIATRY_MODIFICATIONS

  useEffect(() => { load() }, [caseId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Revoke ALL thumbnail object URLs once, on unmount only (reads the ref so it
  // never revokes URLs still in use mid-session — the earlier [photoUrls]-keyed
  // cleanup revoked live URLs on every incremental load, blanking thumbnails and
  // dropping photos from the PDF).
  useEffect(() => () => { Object.values(photoUrlsRef.current).forEach(u => URL.revokeObjectURL(u)) }, [])

  // Replace the tracked URL map, revoking any URLs being dropped.
  const setPhotoUrlMap = useCallback((next) => {
    const prev = photoUrlsRef.current
    for (const [id, url] of Object.entries(prev)) if (next[id] !== url) URL.revokeObjectURL(url)
    photoUrlsRef.current = next
    setPhotoUrls(next)
  }, [])

  async function load() {
    setLoading(true); setError(null)
    try {
      const res = await getPodiatryCase(caseId)
      const d = res.data
      setData(d)
      const i = d.intake || {}
      const loaded = {
        orderNo: i.orderNo || '',
        clinicName: i.clinicName || '', clinicianName: i.clinicianName || '', clinicPhone: i.clinicPhone || '',
        patientName: i.patientName || '', patientAge: i.patientAge ?? '', patientGender: i.patientGender || '',
        heightCm: i.heightCm ?? '', weightKg: i.weightKg ?? '',
        diagnosis: i.diagnosis || '',
        archLeft: i.archLeft || '', archRight: i.archRight || '',
        mobilityLeft: i.mobilityLeft || '', mobilityRight: i.mobilityRight || '',
        offloadAreas: i.offloadAreas || '', offloadMethods: i.offloadMethods || '',
        productCode: i.productCode || '',
        modifications: Array.isArray(i.modificationsJson) ? i.modificationsJson : [],
        footwearSize: i.footwearSize || '', footwearColour: i.footwearColour || '',
        quickNotes: i.quickNotes || '',
        plannedDelivery: i.plannedDelivery ? i.plannedDelivery.slice(0, 10) : '',
      }
      setForm(loaded)
      savedFormRef.current = JSON.stringify(loaded)
      // Measurement table: resolved values (manual ?? auto) per foot.
      const m = { RIGHT: {}, LEFT: {} }
      for (const foot of FEET) {
        const values = d.resolvedMeasurements?.[foot]?.values || {}
        for (const [k, v] of Object.entries(values)) m[foot][k] = String(v.valueCm)
      }
      setMeas(m)
      setDirtyMeas({ RIGHT: new Set(), LEFT: new Set() })
      loadThumbnails(d.intake?.photos || [])
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the podiatry case.')
    } finally { setLoading(false) }
  }

  async function loadThumbnails(photos) {
    // Fetch all photo blobs in parallel (was sequential — 8 photos × RTT of
    // 'Loading…' before the operator could reliably generate a complete PDF),
    // then swap the URL map in ONE update so the old blobs are revoked cleanly.
    const targets = photos.filter(p => p.status === 'UPLOADED' && p.kind === 'PHOTO')
    const settled = await Promise.allSettled(targets.map(p => fetchPodiatryPhotoBlob(p.id)))
    const next = {}
    settled.forEach((r, i) => { if (r.status === 'fulfilled') next[targets[i].id] = URL.createObjectURL(r.value.data) })
    setPhotoUrlMap(next)
  }

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target ? e.target.value : e }))

  function toggleModification(mod) {
    setForm(f => ({
      ...f,
      modifications: f.modifications.includes(mod)
        ? f.modifications.filter(x => x !== mod)
        : [...f.modifications, mod],
    }))
  }

  function setMeasValue(foot, key, value) {
    setMeas(m => ({ ...m, [foot]: { ...m[foot], [key]: value } }))
    setDirtyMeas(d => { const next = { ...d, [foot]: new Set(d[foot]) }; next[foot].add(key); return next })
  }

  async function handleSave(markCompleted) {
    setSaving(true); setError(null)
    try {
      // 1. Intake fields.
      await savePodiatryIntake(caseId, {
        ...form,
        patientAge: form.patientAge === '' ? null : Number(form.patientAge),
        heightCm: form.heightCm === '' ? null : Number(form.heightCm),
        weightKg: form.weightKg === '' ? null : Number(form.weightKg),
        modificationsJson: form.modifications,
        plannedDelivery: form.plannedDelivery || null,
        ...(markCompleted !== undefined ? { markCompleted } : {}),
      })
      // 2. Edited measurements only (per foot) → manual overrides written to the
      //    durable manual layer (the server ignores any session folder). An empty
      //    edited field clears the override.
      for (const foot of FEET) {
        if (dirtyMeas[foot].size === 0) continue
        const manualJson = {}
        for (const key of dirtyMeas[foot]) {
          const raw = (meas[foot][key] ?? '').trim()
          const param = params.find(p => p.key === key)
          manualJson[key] = raw === '' ? null
            : { valueCm: Number(raw), method: param?.physicalOnly ? 'PHYSICAL' : 'MANUAL' }
        }
        await savePodiatryMeasurements(caseId, { foot, manualJson })
      }
      setSavedTick(true); setTimeout(() => setSavedTick(false), 2000)
      await load()
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed.')
    } finally { setSaving(false) }
  }

  async function handleDeletePhoto(photo) {
    if (!window.confirm(`Remove this ${photo.kind === 'ARTIFACT' ? 'artifact' : 'photo'} from the case? The Drive file is kept.`)) return
    try { await deletePodiatryPhoto(photo.id); await load() }
    catch (err) { alert(err.response?.data?.error || 'Delete failed.') }
  }

  // Artifact upload: direct-to-Drive resumable PUT from the browser (same pattern
  // as the DICOM upload — bytes never touch Cloud Run).
  async function handleArtifactFiles(e) {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    setArtifactBusy(true); setError(null)
    try {
      const init = await podiatryArtifactsBatchInit(caseId,
        files.map(f => ({ filename: f.name, fileSize: f.size })))
      const uploads = init.data.uploads || []
      const results = []
      for (const u of uploads) {
        const file = files.find(f => f.name === u.filename)
        if (!file) continue
        const put = await fetch(u.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/octet-stream' },
          body: file,
        })
        if (!put.ok) throw new Error(`Upload failed (${put.status})`)
        const body = await put.json()
        results.push({ uploadId: u.uploadId, driveFileId: body.id, fileSize: file.size })
      }
      await podiatryArtifactsBatchComplete(caseId, results)
      await load()
    } catch (err) {
      setError(err.message || 'Artifact upload failed.')
    } finally {
      setArtifactBusy(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const hasUnsaved = () =>
    dirtyMeas.RIGHT.size > 0 || dirtyMeas.LEFT.size > 0 ||
    JSON.stringify(form) !== savedFormRef.current

  // ── PDF export (lazy jsPDF, same pattern as the Case Report tool) ──
  // The PDF is the manufacturing order that leaves the building — it must match
  // the saved record, never unsaved edit state. Save first if anything is dirty.
  async function generatePdf() {
    if (hasUnsaved()) {
      if (!window.confirm('You have unsaved changes. Save them before generating the order PDF?')) return
      await handleSave()
    }
    setPdfBusy(true)
    try {
      const { jsPDF } = await import('jspdf')
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
      const W = 210, M = 14
      let y = 16

      const line = (h = 4) => { y += h }
      const ensure = (need) => { if (y + need > 283) { doc.addPage(); y = 16 } }
      const label = (t, x = M) => { doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(120); doc.text(t, x, y) }
      const value = (t, x = M) => { doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5); doc.setTextColor(20); doc.text(String(t ?? '—') || '—', x, y) }
      const section = (t) => {
        ensure(14); y += 4
        doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(30, 150, 200)
        doc.text(t, M, y); y += 1.5
        doc.setDrawColor(30, 150, 200); doc.setLineWidth(0.3); doc.line(M, y, W - M, y); y += 6
      }

      // Header
      doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(20)
      doc.text('Custom Orthotic Order', M, y)
      doc.setFontSize(10); doc.setTextColor(120)
      doc.text(`Case ${caseId}${form.orderNo ? `  ·  Order ${form.orderNo}` : ''}`, W - M, y, { align: 'right' }); y += 5
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5)
      doc.text(`Generated ${new Date().toLocaleString()} · Osteo3d Forma-Podiatry`, M, y); y += 2

      section('Clinic & Patient')
      const cols = [M, 80, 145]
      label('Clinic', cols[0]); label('Clinician', cols[1]); label('Phone', cols[2]); line()
      value(form.clinicName, cols[0]); value(form.clinicianName, cols[1]); value(form.clinicPhone, cols[2]); line(7)
      label('Patient', cols[0]); label('Age / Gender', cols[1]); label('Height / Weight', cols[2]); line()
      value(form.patientName, cols[0])
      value([form.patientAge, form.patientGender].filter(Boolean).join(' / '), cols[1])
      value([form.heightCm && `${form.heightCm} cm`, form.weightKg && `${form.weightKg} kg`].filter(Boolean).join(' / '), cols[2]); line(6)

      section('Case Sheet')
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(20)
      const diag = doc.splitTextToSize(form.diagnosis || form.quickNotes || '—', W - 2 * M)
      ensure(diag.length * 4.5); doc.text(diag, M, y); y += diag.length * 4.5 + 1

      section('Examination')
      label('Foot arch — Right', M); label('Left', 80); line()
      value(form.archRight || '—', M); value(form.archLeft || '—', 80); line(7)
      label('Joint mobility — Right', M); label('Left', 80); line()
      value(form.mobilityRight || '—', M); value(form.mobilityLeft || '—', 80); line(4)

      section('Foot Measurements (cm)')
      // Table header
      const tX = { p: M, r: 138, l: 168 }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(80)
      doc.text('Parameter', tX.p, y); doc.text('Right', tX.r, y); doc.text('Left', tX.l, y); y += 2
      doc.setDrawColor(200); doc.setLineWidth(0.2); doc.line(M, y, W - M, y); y += 4.5
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5); doc.setTextColor(20)
      const resolved = data?.resolvedMeasurements || {}
      for (const p of params) {
        ensure(6)
        const mark = (foot) => {
          const v = resolved[foot]?.values?.[p.key]
          const edited = dirtyMeas[foot]?.has(p.key)
          if (p.physicalOnly) return ' ‡'
          if (edited || v?.source === 'manual') return ' †'
          if (v?.source === 'auto') return ' *'
          return ''
        }
        doc.text(p.label, tX.p, y)
        doc.text((meas.RIGHT[p.key] || '—') + (meas.RIGHT[p.key] ? mark('RIGHT') : ''), tX.r, y)
        doc.text((meas.LEFT[p.key] || '—') + (meas.LEFT[p.key] ? mark('LEFT') : ''), tX.l, y)
        y += 5.5
      }
      doc.setFontSize(7.5); doc.setTextColor(120)
      doc.text('* auto-measured from 3D scan   † manually entered/verified   ‡ physical examination', M, y); y += 4
      doc.text('3D scan captured non-weight-bearing (handheld orbit). Tape measurements on a standing, loaded foot may read wider.', M, y); y += 2

      // Conditions from the app quick capture
      const conditions = data?.intake?.conditionsJson
      if (conditions && Object.keys(conditions).length > 0) {
        section('Pathology (from scan visit)')
        doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5); doc.setTextColor(20)
        for (const [foot, views] of Object.entries(conditions)) {
          for (const [view, tags] of Object.entries(views)) {
            ensure(5.5)
            doc.text(`${foot === 'LEFT' ? 'Left' : 'Right'} · ${view.toLowerCase()}: ${tags.map(t => CONDITION_LABELS[t] || t).join(', ')}`, M, y)
            y += 5
          }
        }
      }

      // Photo contact sheet
      const photos = (data?.intake?.photos || []).filter(p => p.kind === 'PHOTO' && photoUrls[p.id])
      if (photos.length > 0) {
        section('Photographs')
        const imgW = 42, imgH = 32, gap = 4
        let x = M
        for (const p of photos) {
          if (x + imgW > W - M) { x = M; y += imgH + 9 }
          ensure(imgH + 10)
          try {
            const dataUrl = await blobUrlToJpegDataUrl(photoUrls[p.id], 480)
            doc.addImage(dataUrl, 'JPEG', x, y, imgW, imgH)
            doc.setFontSize(7.5); doc.setTextColor(90)
            doc.text(`${p.foot === 'LEFT' ? 'L' : p.foot === 'RIGHT' ? 'R' : ''} ${p.view ? p.view.toLowerCase() : ''}`.trim(), x, y + imgH + 3.5)
          } catch { /* skip unrenderable photo */ }
          x += imgW + gap
        }
        y += imgH + 9
      }

      section('Offloading & Recommendations')
      label('Areas to offload'); line(); value(form.offloadAreas); line(7)
      label('Offloading methods'); line(); value(form.offloadMethods); line(7)
      label('Product'); line(); value(form.productCode); line(6)
      if (form.modifications.length > 0) {
        doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(20)
        for (const mod of form.modifications) { ensure(5.5); doc.text(`• ${mod}`, M + 2, y); y += 5 }
      }

      section('Footwear & Delivery')
      label('Size', M); label('Colour', 80); label('Planned delivery', 145); line()
      value(form.footwearSize, M); value(form.footwearColour, 80)
      value(form.plannedDelivery ? new Date(form.plannedDelivery).toLocaleDateString() : '—', 145); line(6)

      doc.save(`${caseId}-orthotic-order.pdf`)
    } catch (err) {
      setError('PDF generation failed: ' + (err.message || err))
    } finally { setPdfBusy(false) }
  }

  // ── UI ──────────────────────────────────────────────────────────

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300'
  const photos = (data?.intake?.photos || []).filter(p => p.kind === 'PHOTO')
  const artifacts = (data?.intake?.photos || []).filter(p => p.kind === 'ARTIFACT')

  function segmented(field, options) {
    return (
      <div className="flex gap-1.5">
        {options.map(o => (
          <button key={o} type="button"
            onClick={() => setForm(f => ({ ...f, [field]: f[field] === o ? '' : o }))}
            className={`text-xs px-2.5 py-1.5 rounded-lg border font-medium ${
              form[field] === o
                ? 'bg-brand-500 border-brand-500 text-white'
                : 'border-gray-200 text-gray-500 hover:bg-gray-50'
            }`}>
            {o.charAt(0) + o.slice(1).toLowerCase()}
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="w-full max-w-3xl h-full bg-gray-50 shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Podiatry — Orthotic Order</h2>
            <p className="text-xs text-gray-400 font-mono">{caseId}
              {data?.intake?.status && (
                <span className={`ml-2 px-2 py-0.5 rounded-full font-sans text-[10px] font-medium ${
                  data.intake.status === 'COMPLETED' ? 'bg-green-100 text-green-700'
                    : data.intake.status === 'APP_SUBMITTED' ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {data.intake.status === 'APP_SUBMITTED' ? 'From app' : data.intake.status.toLowerCase()}
                </span>
              )}
              {data?.intake?.skippedInApp && (
                <span className="ml-1 px-2 py-0.5 rounded-full font-sans text-[10px] font-medium bg-amber-100 text-amber-700">3D-only in app</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={generatePdf} disabled={pdfBusy || loading}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium disabled:opacity-50">
              {pdfBusy ? 'Generating…' : 'Order PDF'}
            </button>
            <button onClick={() => handleSave()} disabled={saving || loading}
              className="btn-primary text-xs disabled:opacity-50">
              {saving ? 'Saving…' : savedTick ? 'Saved ✓' : 'Save'}
            </button>
            <button onClick={() => handleSave(true)} disabled={saving || loading}
              className="text-xs px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 font-medium disabled:opacity-50">
              Save + Complete
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none px-1">×</button>
          </div>
        </div>

        {error && <p className="text-sm text-red-500 px-6 pt-3">{error}</p>}

        {loading ? (
          <div className="py-24 text-center text-gray-400 text-sm">Loading…</div>
        ) : (
          <div className="px-6 py-5 space-y-6">

            {/* Clinic + patient */}
            <Card title="Clinic details">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Order No"><input className={inputCls} value={form.orderNo} onChange={set('orderNo')} placeholder="clinic's order #" /></Field>
                <Field label="Clinic name"><input className={inputCls} value={form.clinicName} onChange={set('clinicName')} /></Field>
                <Field label="Clinician"><input className={inputCls} value={form.clinicianName} onChange={set('clinicianName')} /></Field>
                <Field label="Phone"><input className={inputCls} value={form.clinicPhone} onChange={set('clinicPhone')} /></Field>
              </div>
            </Card>

            <Card title="Patient details">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Name"><input className={inputCls} value={form.patientName} onChange={set('patientName')} /></Field>
                <Field label="Age"><input type="number" className={inputCls} value={form.patientAge} onChange={set('patientAge')} /></Field>
                <Field label="Gender">
                  <select className={inputCls} value={form.patientGender} onChange={set('patientGender')}>
                    <option value="">—</option><option>Female</option><option>Male</option><option>Other</option>
                  </select>
                </Field>
                <Field label="Height (cm)"><input type="number" className={inputCls} value={form.heightCm} onChange={set('heightCm')} /></Field>
                <Field label="Weight (kg)"><input type="number" className={inputCls} value={form.weightKg} onChange={set('weightKg')} /></Field>
              </div>
            </Card>

            <Card title="Case sheet">
              <textarea className={`${inputCls} min-h-[70px]`} value={form.diagnosis} onChange={set('diagnosis')}
                placeholder="Diagnosis / observations, e.g. RT 1st MTPJ arthritis and severe pain on ROM and palpation" />
              {form.quickNotes && (
                <p className="text-xs text-gray-400 mt-2">From app: {form.quickNotes}</p>
              )}
            </Card>

            <Card title="Examination">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-2 font-medium">Foot arch</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3"><span className="text-xs w-10 text-gray-500">Right</span>{segmented('archRight', ARCH_TYPES)}</div>
                    <div className="flex items-center gap-3"><span className="text-xs w-10 text-gray-500">Left</span>{segmented('archLeft', ARCH_TYPES)}</div>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-2 font-medium">Joint mobility</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3"><span className="text-xs w-10 text-gray-500">Right</span>{segmented('mobilityRight', MOBILITY_TYPES)}</div>
                    <div className="flex items-center gap-3"><span className="text-xs w-10 text-gray-500">Left</span>{segmented('mobilityLeft', MOBILITY_TYPES)}</div>
                  </div>
                </div>
              </div>
            </Card>

            <Card title="Foot measurements (cm)">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] text-gray-400 uppercase tracking-wide">
                    <th className="text-left py-1.5 font-medium">Parameter</th>
                    <th className="text-center py-1.5 font-medium w-28">Right</th>
                    <th className="text-center py-1.5 font-medium w-28">Left</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {params.map(p => (
                    <tr key={p.key}>
                      <td className="py-1.5 text-gray-700">
                        {p.label}
                        {p.physicalOnly && <span className="ml-1 text-[10px] text-amber-600" title="Physical examination — not measurable from the 3D scan">‡ exam</span>}
                        {p.autoCapable && <span className="ml-1 text-[10px] text-gray-300" title="Will be auto-measured from the 3D scan (coming)">*</span>}
                      </td>
                      {FEET.map(foot => {
                        const src = data?.resolvedMeasurements?.[foot]?.values?.[p.key]?.source
                        return (
                          <td key={foot} className="py-1 px-1">
                            <input
                              type="number" step="0.1" min="0"
                              className={`w-full border rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-brand-300 ${
                                src === 'auto' && !dirtyMeas[foot].has(p.key) ? 'border-blue-200 bg-blue-50/50' : 'border-gray-200'
                              }`}
                              value={meas[foot][p.key] ?? ''}
                              onChange={(e) => setMeasValue(foot, p.key, e.target.value)}
                              title={src === 'auto' ? 'Auto-measured from the 3D scan — edit to override' : undefined}
                            />
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-gray-400 mt-2">
                ‡ = physical-exam value (enter manually). Blue cells are auto-measured from the 3D scan; editing stores a manual override.
                Scan is captured non-weight-bearing — standing tape girths may read wider.
              </p>
            </Card>

            <Card title={`Pathology photos (${photos.length})`}>
              {photos.length === 0 ? (
                <p className="text-sm text-gray-400">No photos from the app for this case.</p>
              ) : (
                <div className="grid grid-cols-4 gap-3">
                  {photos.map(p => (
                    <div key={p.id} className="relative group">
                      {photoUrls[p.id] ? (
                        <img src={photoUrls[p.id]} alt={`${p.foot} ${p.view}`}
                          className="w-full h-28 object-cover rounded-lg border border-gray-200" />
                      ) : (
                        <div className="w-full h-28 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                          {p.status === 'UPLOADED' ? 'Loading…' : p.status.toLowerCase()}
                        </div>
                      )}
                      <p className="text-[11px] text-gray-500 mt-1">
                        {(p.foot === 'LEFT' ? 'Left' : p.foot === 'RIGHT' ? 'Right' : '')} {p.view ? p.view.toLowerCase() : ''}
                        {p.conditionTags?.length > 0 && (
                          <span className="text-amber-600"> · {p.conditionTags.map(t => CONDITION_LABELS[t] || t).join(', ')}</span>
                        )}
                      </p>
                      <button onClick={() => handleDeletePhoto(p)}
                        className="absolute top-1 right-1 hidden group-hover:block bg-black/50 text-white rounded-full w-5 h-5 text-xs leading-none">×</button>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card title="Offloading">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Areas to offload"><input className={inputCls} value={form.offloadAreas} onChange={set('offloadAreas')} /></Field>
                <Field label="Offloading methods"><input className={inputCls} value={form.offloadMethods} onChange={set('offloadMethods')} /></Field>
              </div>
            </Card>

            <Card title="Recommendations">
              <Field label="Product (model + colour)">
                <input className={inputCls} value={form.productCode} onChange={set('productCode')} placeholder="e.g. M5 Brown" />
              </Field>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-3">
                {modifications.map(mod => (
                  <label key={mod} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" className="accent-brand-500"
                      checked={form.modifications.includes(mod)}
                      onChange={() => toggleModification(mod)} />
                    {mod}
                  </label>
                ))}
              </div>
            </Card>

            <Card title="Footwear & delivery">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Size"><input className={inputCls} value={form.footwearSize} onChange={set('footwearSize')} placeholder="e.g. L5" /></Field>
                <Field label="Colour"><input className={inputCls} value={form.footwearColour} onChange={set('footwearColour')} placeholder="e.g. Brown" /></Field>
                <Field label="Planned delivery date"><input type="date" className={inputCls} value={form.plannedDelivery} onChange={set('plannedDelivery')} /></Field>
              </div>
            </Card>

            <Card title={`Supporting artifacts (${artifacts.length})`}>
              {artifacts.length > 0 && (
                <ul className="text-sm text-gray-700 space-y-1 mb-3">
                  {artifacts.map(a => (
                    <li key={a.id} className="flex items-center justify-between">
                      <span>{a.filename} <span className="text-xs text-gray-400">({(Number(a.fileSizeBytes) / 1048576).toFixed(1)} MB · {a.status.toLowerCase()})</span></span>
                      <button onClick={() => handleDeletePhoto(a)} className="text-xs text-red-400 hover:text-red-600">remove</button>
                    </li>
                  ))}
                </ul>
              )}
              <input ref={fileInputRef} type="file" multiple onChange={handleArtifactFiles}
                accept=".pdf,.jpg,.jpeg,.png,.mp4,.mov" className="hidden" id="podiatry-artifact-input" />
              <button onClick={() => fileInputRef.current?.click()} disabled={artifactBusy}
                className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium disabled:opacity-50">
                {artifactBusy ? 'Uploading…' : '+ Upload PDFs / gait videos / sketches'}
              </button>
            </Card>

          </div>
        )}
      </div>
    </div>
  )
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">{title}</h3>
      {children}
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-gray-500 block mb-1">{label}</label>
      {children}
    </div>
  )
}

// Downscale a blob-URL image to a JPEG data URL for jsPDF embedding.
async function blobUrlToJpegDataUrl(url, maxDim) {
  const img = await new Promise((resolve, reject) => {
    const el = new Image()
    el.onload = () => resolve(el)
    el.onerror = reject
    el.src = url
  })
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(img.width * scale)
  canvas.height = Math.round(img.height * scale)
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height)
  return canvas.toDataURL('image/jpeg', 0.85)
}
